import React, { useState } from "react";
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Box,
  Tooltip,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
} from "@mui/material";
import LogoutIcon from "@mui/icons-material/Logout";
import { NeedAssistanceDialog } from "../features/help";
import NotificationBell from "../features/notifications/NotificationBell";

/* ------------------------------------
   Helpers
------------------------------------ */
function getFirstLetter(user) {
  const name = user?.first_name || user?.name || user?.email || "";
  return name?.trim()?.charAt(0)?.toUpperCase() || "U";
}

const BRAND_BLUE = "#00336C";
const BRAND_GREEN = "#648315";
const TOP_NAV_GREEN = "#526f12";

/* ------------------------------------
   Main Component
------------------------------------ */
export default function TopNav({ user, onLogout, onNavigate }) {
  const [userMenuEl, setUserMenuEl] = useState(null);
  const [helpOpen, setHelpOpen] = useState(false);

  const firstLetter = getFirstLetter(user);
  const organizationName = user?.organization_name || "";

  return (
    <AppBar
      position="fixed"
      elevation={0}
      sx={{
        backgroundColor: TOP_NAV_GREEN,
        borderBottom: `3px solid ${TOP_NAV_GREEN}`,
        zIndex: (theme) => theme.zIndex.drawer + 1,
      }}
    >
      <Toolbar
        sx={{
          pl: "72px",
          pr: 2,
          minHeight: 64,
          position: "relative",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        {/* LEFT: Organization Name */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            minWidth: 0,
            flex: 1,
            pr: { xs: 1, sm: 2 },
          }}
        >
          {organizationName && (
            <Tooltip title={organizationName}>
              <Typography
                sx={{
                  color: "#fff",
                  fontWeight: 800,
                  fontSize: "clamp(0.68rem, 1vw, 0.84rem)",
                  lineHeight: 1.1,
                  letterSpacing: 0,
                  textShadow: "0 1px 3px rgba(0,0,0,0.28)",
                  whiteSpace: "nowrap",
                  overflow: "visible",
                  maxWidth: {
                    xs: "calc(100vw - 220px)",
                    sm: "calc(100vw - 430px)",
                  },
                }}
              >
                {organizationName}
              </Typography>
            </Tooltip>
          )}
        </Box>

        {/* CENTER: Logo */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            zIndex: 1,
            pointerEvents: "none",
          }}
        >
          <Typography
            component="div"
            sx={{
              color: "#fff",
              fontWeight: 800,
              fontSize: { xs: "1.18rem", sm: "1.45rem" },
              lineHeight: 1,
              letterSpacing: 0,
              whiteSpace: "nowrap",
              textShadow: "0 2px 5px rgba(0,0,0,0.24)",
            }}
          >
            <Box component="span" sx={{ fontWeight: 900 }}>
              Prospect
            </Box>
            <Box component="span" sx={{ fontWeight: 700 }}>
              Engage
            </Box>
          </Typography>
        </Box>

        {/* RIGHT: Account */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "center",
            gap: 2,
            minWidth: 0,
            flexShrink: 0,
            position: "relative",
            zIndex: 2,
          }}
        >
          <Tooltip title="Help">
            <Button
              variant="contained"
              onClick={() => setHelpOpen(true)}
              sx={{
                minWidth: 0,
                px: 1.5,
                height: 38,
                color: "#fff",
                backgroundColor: "#00336C",
                border: "2px solid #fff",
                borderRadius: 2,
                fontWeight: 800,
                fontSize: "0.8rem",
                textTransform: "none",
                transition: "all .2s ease",
                "&:hover": {
                  backgroundColor: "#0a427f",
                  backdropFilter: "blur(2px)",
                },
              }}
            >
              Need Assistance?
            </Button>
          </Tooltip>

          <NotificationBell onNavigate={onNavigate} />

          <Tooltip title="Account">
            <Button
              variant="outlined"
              onClick={(e) => setUserMenuEl(e.currentTarget)}
              sx={{
                minWidth: 44,
                width: 44,
                height: 44,
                p: 0,
                color: "#fff",
                borderColor: "#fff",
                borderWidth: 2.5,
                fontWeight: 800,
                fontSize: "1.3rem",
                borderRadius: "50%",
                transition: "all .2s ease",
                "&:hover": {
                  borderColor: "#fff",
                  backgroundColor: "rgba(255,255,255,0.15)",
                  backdropFilter: "blur(2px)",
                },
              }}
            >
              {firstLetter}
            </Button>
          </Tooltip>
        </Box>

        {/* USER MENU */}
        <Menu
          anchorEl={userMenuEl}
          open={Boolean(userMenuEl)}
          onClose={() => setUserMenuEl(null)}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          transformOrigin={{ vertical: "top", horizontal: "right" }}
          PaperProps={{
            sx: {
              mt: 1.25,
              minWidth: 230,
              borderRadius: 2,
              border: "1px solid rgba(0,51,108,0.12)",
              boxShadow: "0 16px 40px rgba(15,23,42,0.18)",
              overflow: "hidden",
            },
          }}
          MenuListProps={{
            sx: {
              p: 0.75,
            },
          }}
        >
          <Box sx={{ px: 1.25, py: 1 }}>
            <Typography sx={{ color: BRAND_BLUE, fontWeight: 800, fontSize: "0.86rem" }}>
              {user?.name || user?.email || "Account"}
            </Typography>
            {user?.email && user?.name && (
              <Typography sx={{ color: "#6b7d93", fontSize: "0.76rem", mt: 0.25 }}>
                {user.email}
              </Typography>
            )}
          </Box>
          <Divider sx={{ borderColor: "rgba(0,51,108,0.08)", my: 0.5 }} />
          <MenuItem
            onClick={() => {
              setUserMenuEl(null);
              onLogout();
            }}
            sx={{
              color: BRAND_BLUE,
              borderRadius: 1.5,
              fontWeight: 800,
              minHeight: 42,
              "&:hover": {
                backgroundColor: "rgba(0,51,108,0.06)",
              },
            }}
          >
            <ListItemIcon sx={{ minWidth: 34, color: BRAND_GREEN }}>
              <LogoutIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText
              primary="Logout"
              primaryTypographyProps={{
                sx: { color: BRAND_BLUE, fontWeight: 800, fontSize: "0.9rem" },
              }}
            />
          </MenuItem>
        </Menu>

        <NeedAssistanceDialog
          open={helpOpen}
          onClose={() => setHelpOpen(false)}
          user={user}
        />
      </Toolbar>
    </AppBar>
  );
}
